import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db, auth } from './init';
import {
  buildPublicationSnapshot,
  resolveEntrySettings
} from '../utils/publicationSnapshot';
import { extractP5Version } from '../utils/arSessionPayload';
import type { ARSettings } from '../../common/types/arSettings';

/*
 * Classroom publication — an instructor turns a classroom into ONE public webapp: a QR/URL that opens
 * the AR viewer with a picker over the chosen sketches.
 *
 *   publications/{classroomId}                      the index the public app reads first
 *   publications/{classroomId}/sketches/{projectId} one FROZEN snapshot per entry
 *
 * The publication doc id IS the classroom id, and it is also the public URL (`?g=<classroomId>`):
 * Firestore auto-ids carry ~120 bits of randomness, so the id is the capability (as with an AR session
 * token), and knowing a classroom id grants nothing by itself — the `classrooms` read rule still
 * requires admin or instructor membership. Using it directly is what lets an instructor find their own
 * publication with no pointer field: `classrooms` is `allow write: if false` for clients, so a separate
 * random token would need a Cloud Function just to mint and store it.
 *
 * Snapshots are frozen: a student editing afterwards changes nothing public until the instructor
 * publishes again (or presses Refresh). Conversion runs HERE, in the instructor's browser, exactly like
 * the Run-time AR publish — no Cloud Function involved.
 */

/**
 * One row of the public picker. Both `sketchName` and `label` are instructor-editable and STORED here
 * (prefilled with the project name and the owner's username): editing them changes only the
 * publication, never the underlying project. AR settings are always inherited from the student's own
 * session (no per-entry override).
 */
export interface PublicationEntry {
  projectId: string;
  sketchName: string;
  label: string;
  p5Version: string;
  /** The source sketch is Private — published anyway, but flagged in the publish UI. */
  private: boolean;
}

export interface Publication {
  classroomId: string;
  classroomName: string;
  title: string;
  entries: PublicationEntry[];
  createdBy: string | null;
  updatedAt: number;
}

/** What the publish modal hands over for each checked sketch. */
export interface PublicationSelection {
  projectId: string;
  label: string;
  /** Instructor-chosen display name; falls back to the project name when omitted. */
  sketchName?: string;
}

export interface PublicationError {
  projectId: string;
  sketchName: string;
  message: string;
}

export interface PublishResult {
  publication: Publication;
  /** Per-sketch failures. A failing sketch is skipped; the rest still publish. */
  errors: PublicationError[];
}

interface PublishArgs {
  classroomId: string;
  classroomName: string;
  title: string;
  selections: PublicationSelection[];
  instructorSettings: ARSettings | null;
  onProgress?: (done: number, total: number) => void;
}

function tsToMillis(value: unknown): number {
  if (value && typeof (value as any).toMillis === 'function')
    return (value as any).toMillis();
  if (typeof value === 'number') return value;
  return 0;
}

function publicationRef(classroomId: string) {
  return doc(db, 'publications', classroomId);
}

function sketchesRef(classroomId: string) {
  return collection(db, 'publications', classroomId, 'sketches');
}

function sketchRef(classroomId: string, projectId: string) {
  return doc(db, 'publications', classroomId, 'sketches', projectId);
}

function mapPublication(classroomId: string, data: any): Publication {
  return {
    classroomId,
    classroomName: data.classroomName ?? '',
    title: data.title ?? '',
    entries: (data.entries || []) as PublicationEntry[],
    createdBy: data.createdBy ?? null,
    updatedAt: tsToMillis(data.updatedAt)
  };
}

/** Read a classroom's publication, or null if it has never been published. */
export async function getPublication(
  classroomId: string
): Promise<Publication | null> {
  if (!classroomId) return null;
  const snap = await getDoc(publicationRef(classroomId));
  if (!snap.exists()) return null;
  return mapPublication(classroomId, snap.data());
}

/**
 * Read a project straight by id — not via getProject, which needs the owner's username. The username
 * is deliberately NOT stored on the publication (the public label is the instructor's to choose), so
 * refreshing must work without it. Instructors and admins may read any project under the `projects`
 * read rule.
 */
async function readProject(projectId: string) {
  const snap = await getDoc(doc(db, 'projects', projectId));
  if (!snap.exists()) {
    throw new Error('This sketch no longer exists.');
  }
  return snap.data() as any;
}

/**
 * The p5 version a sketch was authored on, read straight off its `index.html`. The publish modal
 * needs this BEFORE publishing, to warn when the selected set spans versions: the viewer can't
 * hot-swap p5, so a cross-version switch in the published app costs an AR restart.
 */
export async function getSketchP5Version(projectId: string): Promise<string> {
  const project = await readProject(projectId);
  return extractP5Version(project.files || []);
}

/**
 * The student's own AR session doc, if they ever opened this sketch in AR: the source of the settings
 * they tuned for it and of their last slider values. Missing/unreadable → null, so a student who never
 * used AR simply falls back to the instructor's preferences.
 */
async function readStudentSession(arSessionId: string | null | undefined) {
  if (!arSessionId) return null;
  try {
    const snap = await getDoc(doc(db, 'arSessions', arSessionId));
    return snap.exists() ? (snap.data() as any) : null;
  } catch {
    return null;
  }
}

/** Snapshot one selected sketch and write it; returns the index entry to record for it. */
async function snapshotOne(
  classroomId: string,
  selection: PublicationSelection,
  instructorSettings: ARSettings | null
): Promise<PublicationEntry> {
  const project = await readProject(selection.projectId);
  const session = await readStudentSession(project.arSessionId);
  // AR settings always inherit from the student's own session (instructor prefs as fallback) — there
  // is no per-entry override.
  const arSettings = resolveEntrySettings(
    session ? session.arSettings : null,
    instructorSettings
  );
  const snapshot = buildPublicationSnapshot({
    files: project.files || [],
    arSettings,
    sliderValues: session ? session.sliderValues : null
  });

  await setDoc(sketchRef(classroomId, selection.projectId), {
    ...snapshot,
    snapshotAt: serverTimestamp()
  });

  return {
    projectId: selection.projectId,
    // The instructor's chosen name is stored on the entry; it never touches the project doc. Falls
    // back to the current project name when the instructor didn't rename it.
    sketchName: selection.sketchName || project.name,
    label: selection.label,
    p5Version: snapshot.p5Version,
    private: project.visibility === 'Private'
  };
}

/**
 * Publish (or re-publish) a classroom.
 *
 * Snapshots are written one at a time — so `onProgress` means something and a class of 30 doesn't fire
 * 30 concurrent writes — and THE INDEX IS WRITTEN LAST: a half-finished publish must never advertise a
 * sketch whose snapshot isn't there. Snapshots of sketches dropped from the selection are deleted
 * before the index is rewritten, so unpublishing one sketch really does take it offline.
 *
 * A sketch that fails (deleted project, unconvertible code, payload over Firestore's 1 MB document
 * limit) lands in `errors` and is skipped; the remaining sketches still publish.
 */
export async function publishClassroom({
  classroomId,
  classroomName,
  title,
  selections,
  instructorSettings,
  onProgress
}: PublishArgs): Promise<PublishResult> {
  const uid = auth.currentUser?.uid ?? null;
  const previous = await getPublication(classroomId);

  const entries: PublicationEntry[] = [];
  const errors: PublicationError[] = [];

  for (let i = 0; i < selections.length; i += 1) {
    const selection = selections[i];
    try {
      // Sequential on purpose (see above).
      // eslint-disable-next-line no-await-in-loop
      const entry = await snapshotOne(
        classroomId,
        selection,
        instructorSettings
      );
      entries.push(entry);
    } catch (e) {
      errors.push({
        projectId: selection.projectId,
        sketchName: selection.label || selection.projectId,
        message: (e as Error).message || 'Could not publish this sketch.'
      });
    }
    if (onProgress) onProgress(i + 1, selections.length);
  }

  const kept = new Set(entries.map((entry) => entry.projectId));
  const stale = (previous?.entries || [])
    .map((entry) => entry.projectId)
    .filter((projectId) => !kept.has(projectId));
  for (let i = 0; i < stale.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await deleteDoc(sketchRef(classroomId, stale[i]));
  }

  const createdBy = previous ? previous.createdBy : uid;
  await setDoc(
    publicationRef(classroomId),
    {
      classroomId,
      classroomName,
      title,
      entries,
      createdBy,
      updatedAt: serverTimestamp(),
      ...(previous ? {} : { createdAt: serverTimestamp() })
    },
    { merge: true }
  );

  return {
    publication: {
      classroomId,
      classroomName,
      title,
      entries,
      createdBy,
      updatedAt: Date.now()
    },
    errors
  };
}

/**
 * Re-snapshot everything already published, keeping each entry's instructor-chosen name, label and
 * order. Only the sketch CODE and p5 version are re-read, so a code edit is picked up while the
 * published name/label the instructor set stay put. An entry whose project has since been deleted is
 * dropped and reported.
 */
export async function refreshPublication({
  classroomId,
  instructorSettings,
  onProgress
}: {
  classroomId: string;
  instructorSettings: ARSettings | null;
  onProgress?: (done: number, total: number) => void;
}): Promise<PublishResult> {
  const previous = await getPublication(classroomId);
  if (!previous) {
    throw new Error('This classroom is not published yet.');
  }
  return publishClassroom({
    classroomId,
    classroomName: previous.classroomName,
    title: previous.title,
    selections: previous.entries.map((entry) => ({
      projectId: entry.projectId,
      label: entry.label,
      sketchName: entry.sketchName
    })),
    instructorSettings,
    onProgress
  });
}

/**
 * Take the published app offline: delete every snapshot, then the index. Snapshots go first so the
 * index never outlives its content — and the subcollection is ENUMERATED rather than read off
 * `entries`, which also sweeps up a snapshot that a half-finished publish left unlisted.
 */
export async function unpublishClassroom(classroomId: string): Promise<void> {
  const snap = await getDocs(sketchesRef(classroomId));
  for (let i = 0; i < snap.docs.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await deleteDoc(sketchRef(classroomId, snap.docs[i].id));
  }
  await deleteDoc(publicationRef(classroomId));
}
